import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { XCircle, ExternalLink, Phone, Mail, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const DIFFICULTY_TONE = {
    easy: "bg-emerald-50 text-emerald-700 border-emerald-200",
    moderate: "bg-amber-50 text-amber-700 border-amber-200",
    hard: "bg-red-50 text-red-700 border-red-200",
};

/**
 * Subscription cancellation dialog — shows the curated guide, then lets the
 * user record the cancellation with reason + difficulty rating. Reports the
 * annual saving that was locked in.
 */
export default function SubscriptionCancelDialog({ open, onOpenChange, subscription, onCancelled }) {
    const [guide, setGuide] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("Unused");
    const [difficulty, setDifficulty] = useState("");
    const [remarks, setRemarks] = useState("");

    useEffect(() => {
        if (!open || !subscription) return;
        setGuide(null);
        api.get(`/subscriptions/${subscription.id}/cancel-guide`)
            .then((r) => setGuide(r.data))
            .catch(() => setGuide(null));
    }, [open, subscription]);

    if (!subscription) return null;

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/subscriptions/${subscription.id}/cancel`, {
                reason, difficulty_actual: difficulty, remarks,
            });
            toast.success(`Cancelled · saving ${fmtMoney(data.annual_saving)}/yr`);
            onOpenChange(false);
            onCancelled?.(data);
            setReason("Unused"); setDifficulty(""); setRemarks("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sub-cancel-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <XCircle size={20} className="text-red-600" />
                        Cancel {subscription.name}
                    </DialogTitle>
                    <DialogDescription className="text-[13px]">
                        {guide?.estimated_annual_saving ? (
                            <span className="text-stone-700">
                                Cancelling this saves <strong className="text-emerald-700">{fmtMoney(guide.estimated_annual_saving)}/yr</strong>.
                            </span>
                        ) : "Ending this subscription."}
                    </DialogDescription>
                </DialogHeader>

                {!guide ? (
                    <div className="py-6 text-sm text-stone-500">Loading guide…</div>
                ) : (
                    <div className="space-y-4">
                        {/* Guide banner */}
                        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="eyebrow">Cancellation guide · {guide.guide.category}</div>
                                    <div className="font-display text-lg text-stone-900 mt-1">{guide.guide.name}</div>
                                    <div className="text-xs text-stone-600 mt-1">{guide.guide.method}</div>
                                </div>
                                <div className="flex flex-col items-end gap-1.5">
                                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${DIFFICULTY_TONE[guide.guide.difficulty] || DIFFICULTY_TONE.moderate}`}>
                                        {guide.guide.difficulty}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[11px] text-stone-500">
                                        <Clock size={11} /> ~{guide.guide.estimated_min} min
                                    </span>
                                </div>
                            </div>

                            {/* Contacts */}
                            <div className="flex flex-wrap gap-2 mt-3">
                                {guide.guide.cancel_url && (
                                    <a href={guide.guide.cancel_url} target="_blank" rel="noreferrer"
                                       className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border border-stone-300 bg-white hover:border-stone-900 transition-colors"
                                       data-testid="sub-cancel-open-url">
                                        <ExternalLink size={11} /> Open cancel page
                                    </a>
                                )}
                                {guide.guide.phone && (
                                    <a href={`tel:${guide.guide.phone}`}
                                       className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border border-stone-300 bg-white hover:border-stone-900 transition-colors">
                                        <Phone size={11} /> {guide.guide.phone}
                                    </a>
                                )}
                                {guide.guide.email && (
                                    <a href={`mailto:${guide.guide.email}?subject=Cancellation Request`}
                                       className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full border border-stone-300 bg-white hover:border-stone-900 transition-colors">
                                        <Mail size={11} /> Email support
                                    </a>
                                )}
                            </div>

                            {/* Steps */}
                            {guide.guide.steps?.length > 0 && (
                                <ol className="mt-4 list-decimal ml-4 space-y-1 text-[13px] text-stone-700">
                                    {guide.guide.steps.map((s, i) => <li key={i}>{s}</li>)}
                                </ol>
                            )}

                            {/* Caveats */}
                            {guide.guide.caveats?.length > 0 && (
                                <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-900">
                                    <div className="flex items-center gap-1.5 font-medium mb-1">
                                        <AlertTriangle size={12} /> Watch out for
                                    </div>
                                    <ul className="list-disc ml-4 space-y-0.5">
                                        {guide.guide.caveats.map((c, i) => <li key={i}>{c}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Record cancellation */}
                        <div className="rounded-lg border border-stone-200 bg-white p-4 space-y-3">
                            <div className="eyebrow flex items-center gap-1.5">
                                <CheckCircle2 size={11} className="text-emerald-600" /> Once cancelled, record it here
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="eyebrow">Reason</Label>
                                    <Select value={reason} onValueChange={setReason}>
                                        <SelectTrigger className="mt-1.5" data-testid="sub-cancel-reason"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Unused">Unused</SelectItem>
                                            <SelectItem value="Too expensive">Too expensive</SelectItem>
                                            <SelectItem value="Better alternative">Better alternative</SelectItem>
                                            <SelectItem value="Temporary pause">Temporary pause</SelectItem>
                                            <SelectItem value="Duplicate service">Duplicate service</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Difficulty (rate the process)</Label>
                                    <Select value={difficulty} onValueChange={setDifficulty}>
                                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Optional" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="easy">Easy</SelectItem>
                                            <SelectItem value="moderate">Moderate</SelectItem>
                                            <SelectItem value="hard">Hard</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="eyebrow">Remarks</Label>
                                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                                          placeholder="Reference number, agent name, retention offers…" className="mt-1.5" />
                            </div>
                            <Button onClick={submit} disabled={busy}
                                    className="w-full bg-red-700 hover:bg-red-800 text-white"
                                    data-testid="sub-cancel-confirm-btn">
                                {busy ? "Recording…" : `Mark cancelled · save ${fmtMoney(guide.estimated_annual_saving)}/yr`}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
